// Piñata sound effects, synthesized with Web Audio so there are no audio files
// to host. Every sound is built from a noise burst and/or an oscillator.

const MUTE_KEY = 'pinata:muted'

// ---- mute preference (read with useSyncExternalStore) ----------------------
let muted = readMuted()
const muteListeners = new Set<() => void>()

function readMuted() {
	try {
		return typeof window !== 'undefined' && window.localStorage.getItem(MUTE_KEY) === '1'
	} catch {
		return false
	}
}

export const sfxMute = {
	get: () => muted,
	getServer: () => false,
	subscribe(listener: () => void) {
		muteListeners.add(listener)
		return () => void muteListeners.delete(listener)
	},
	set(next: boolean) {
		muted = next
		try {
			window.localStorage.setItem(MUTE_KEY, next ? '1' : '0')
		} catch {}
		muteListeners.forEach((l) => l())
	},
}

// ---- synth -----------------------------------------------------------------
let ctx: AudioContext | null = null
let master: GainNode | null = null
let noise: AudioBuffer | null = null

function audio() {
	if (!ctx) {
		ctx = new AudioContext()
		master = ctx.createGain()
		master.gain.value = 0.55
		master.connect(ctx.destination)
		noise = ctx.createBuffer(1, ctx.sampleRate, ctx.sampleRate)
		const data = noise.getChannelData(0)
		for (let i = 0; i < data.length; i++) data[i] = Math.random() * 2 - 1
	}
	if (ctx.state === 'suspended') void ctx.resume()
	return { ctx, out: master!, noise: noise! }
}

function envelope(gain: GainNode, at: number, peak: number, decay: number) {
	gain.gain.setValueAtTime(0.0001, at)
	gain.gain.exponentialRampToValueAtTime(peak, at + 0.004)
	gain.gain.exponentialRampToValueAtTime(0.0001, at + decay)
}

function noiseBurst(opts: {
	at: number
	type: BiquadFilterType
	freq: number
	freqEnd?: number
	q?: number
	peak: number
	decay: number
}) {
	const { ctx, out, noise } = audio()
	const src = ctx.createBufferSource()
	src.buffer = noise
	src.playbackRate.value = 0.8 + Math.random() * 0.4
	const filter = ctx.createBiquadFilter()
	filter.type = opts.type
	filter.Q.value = opts.q ?? 1
	filter.frequency.setValueAtTime(opts.freq, opts.at)
	if (opts.freqEnd) filter.frequency.exponentialRampToValueAtTime(opts.freqEnd, opts.at + opts.decay)
	const gain = ctx.createGain()
	envelope(gain, opts.at, opts.peak, opts.decay)
	src.connect(filter).connect(gain).connect(out)
	src.start(opts.at, Math.random() * 0.5)
	src.stop(opts.at + opts.decay + 0.05)
}

function tone(opts: {
	at: number
	type: OscillatorType
	freq: number
	freqEnd?: number
	peak: number
	decay: number
}) {
	const { ctx, out } = audio()
	const osc = ctx.createOscillator()
	osc.type = opts.type
	osc.frequency.setValueAtTime(opts.freq, opts.at)
	if (opts.freqEnd) osc.frequency.exponentialRampToValueAtTime(opts.freqEnd, opts.at + opts.decay)
	const gain = ctx.createGain()
	envelope(gain, opts.at, opts.peak, opts.decay)
	osc.connect(gain).connect(out)
	osc.start(opts.at)
	osc.stop(opts.at + opts.decay + 0.05)
}

export type SfxName = 'hit' | 'weak' | 'crit' | 'crunch' | 'boom' | 'whoosh'

export function playSfx(name: SfxName, combo = 0) {
	if (muted || typeof window === 'undefined' || !('AudioContext' in window)) return
	const now = audio().ctx.currentTime
	const pitch = 1 + Math.min(combo, 5) * 0.07

	switch (name) {
		case 'hit':
			// Papier-mâché thwack: a low thump plus a papery slap.
			tone({ at: now, type: 'sine', freq: 170 * pitch, freqEnd: 55, peak: 0.9, decay: 0.16 })
			noiseBurst({ at: now, type: 'bandpass', freq: 1700 * pitch, q: 0.8, peak: 0.7, decay: 0.09 })
			break
		case 'weak':
			// A soft, dull tap: clearly lesser than a real thwack.
			tone({ at: now, type: 'sine', freq: 120, freqEnd: 60, peak: 0.35, decay: 0.1 })
			noiseBurst({ at: now, type: 'lowpass', freq: 900, peak: 0.25, decay: 0.06 })
			break
		case 'crit':
			// A heavier thwack with a crack on top.
			tone({ at: now, type: 'sine', freq: 140, freqEnd: 40, peak: 1, decay: 0.22 })
			noiseBurst({ at: now, type: 'bandpass', freq: 1400, q: 0.7, peak: 0.9, decay: 0.12 })
			noiseBurst({ at: now, type: 'highpass', freq: 4000, peak: 0.5, decay: 0.05 })
			break
		case 'crunch':
			for (let i = 0; i < 6; i++) {
				noiseBurst({
					at: now + 0.02 + Math.random() * 0.14,
					type: 'highpass',
					freq: 2200 + Math.random() * 1800,
					peak: 0.35,
					decay: 0.03 + Math.random() * 0.03,
				})
			}
			break
		case 'boom':
			tone({ at: now, type: 'sine', freq: 110, freqEnd: 28, peak: 1, decay: 0.7 })
			noiseBurst({ at: now, type: 'lowpass', freq: 2400, freqEnd: 110, peak: 1, decay: 1.2 })
			// A little fanfare once the dust settles: C major arpeggio.
			;[523.25, 659.25, 783.99, 1046.5].forEach((freq, i) => {
				tone({ at: now + 0.25 + i * 0.11, type: 'triangle', freq, peak: 0.28, decay: i === 3 ? 0.6 : 0.2 })
			})
			break
		case 'whoosh':
			noiseBurst({ at: now, type: 'bandpass', freq: 350, freqEnd: 1600, q: 1.4, peak: 0.25, decay: 0.16 })
			break
	}
}
